/**
 * Peak-heap sampling for the export, so "does this survive a long video" is a
 * number rather than a guess.
 *
 * performance.memory is Chrome-only and non-standard — it is deliberately not
 * in lib.dom. It reports the JS heap only, which matters: WebCodecs frame
 * buffers and the muxed output live largely *outside* it, so a flat reading
 * here alongside a tab crash points at those rather than at our own code.
 */
type ChromeMemory = {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
};

const readHeap = (): ChromeMemory | null => {
  const perf = performance as Performance & { memory?: ChromeMemory };
  return perf.memory ?? null;
};

export type HeapSample = { usedMb: number; limitMb: number; pct: number };

const sampleHeap = (): HeapSample | null => {
  const memory = readHeap();
  if (!memory) return null;
  return {
    usedMb: memory.usedJSHeapSize / 1048576,
    limitMb: memory.jsHeapSizeLimit / 1048576,
    pct: memory.usedJSHeapSize / memory.jsHeapSizeLimit,
  };
};

/** Tracks the high-water mark across a long operation. */
export const createHeapTracker = () => {
  let peak: HeapSample | null = null;
  return {
    sample() {
      const current = sampleHeap();
      if (current && (!peak || current.usedMb > peak.usedMb)) peak = current;
      return current;
    },
    peak: () => peak,
  };
};

export const formatPeak = (peak: HeapSample | null): string =>
  peak === null
    ? 'heap size unavailable in this browser'
    : `peak heap ${peak.usedMb.toFixed(0)} MB of ${peak.limitMb.toFixed(0)} MB (${(peak.pct * 100).toFixed(0)}%)`;
